import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { useController } from "react-hook-form";
import * as React from "react";
import { Search, X, MapPin, Loader2, AlertCircle } from "lucide-react";

import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { HiddenField } from "@/components/fields/hidden-field";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ADDRESS_SEARCH_VALIDATION,
  ADDRESS_SEARCH_TIMING,
  ADDRESS_SEARCH_DEFAULTS,
  KEYBOARD_KEYS,
  ADDRESS_SEARCH_MESSAGES,
  ENTRY_METHODS,
} from "@/lib/address-search-constants";
import {
  AddressSearchTimeoutError,
  AddressSearchApiError,
  AddressSearchPredictionError,
  isAbortError,
  getUserFriendlyErrorMessage,
  logAddressSearchError,
} from "@/lib/address-search-errors";
import { useAddressSearchState } from "@/lib/address-search-state";
import type { CountryCode } from "@/lib/address-search-types";
import type {
  BusinessPrediction,
  BusinessComponents,
} from "@/lib/business-search-types";
import { cn } from "@/lib/utils";
import { FormTooltip } from "@/components/ui/form-description";

export type BusinessSearchAssistiveMessages = {
  searching?: string;
  noResults?: string;
  error?: string;
  resultsFound?: (count: number) => string;
};

export interface BusinessSearchFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  descriptionAsTooltip?: boolean;
  required?: boolean;
  requiredLabel?: string;
  country?: CountryCode;
  businessTypeOptions?: { value: string; label: string }[];
  stateOptions?: { value: string; label: string }[];
  showAddressLine2?: boolean;
  postalCodeLabel?: string;
  postalCodePlaceholder?: string;
  searchDebounceMs?: number;
  maxResults?: number;
  apiEndpoint?: string;
  manualEntryCheckboxLabel?: string;
  allowManualEntry?: boolean;
  searchMode?: "postal-code" | "business-name";
  searchPlaceholder?: string;
  searchLabel?: string;
  businessTypeLabel?: string;
  businessTypePlaceholder?: string;
  businessNameLabel?: string;
  businessNamePlaceholder?: string;
  addressLine1Label?: string;
  addressLine1Placeholder?: string;
  addressLine2Label?: string;
  addressLine2Placeholder?: string;
  cityLabel?: string;
  cityPlaceholder?: string;
  stateLabel?: string;
  statePlaceholder?: string;
  noResultsMessage?: string;
  errorFallbackMessage?: string;
  errorMessageOverride?: string;
  selectedBusinessHeading?: string;
  showSelectedBusinessType?: boolean;
  selectedBusinessDismissLabel?: string;
  selectedBusinessNotCorrectLabel?: string;
  selectedBusinessTryAgainLabel?: string;
  inputParamName?: string;
  countryParamName?: string;
  businessTypeParamName?: string;
  businessTypeFilterOptions?: string;
  srMessages?: BusinessSearchAssistiveMessages;
}

export function BusinessSearchField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  descriptionAsTooltip = false,
  required = false,
  requiredLabel = "Required",
  country = "US",
  businessTypeOptions = [],
  stateOptions,
  showAddressLine2 = true,
  searchDebounceMs = ADDRESS_SEARCH_TIMING.DEFAULT_DEBOUNCE_MS,
  maxResults = ADDRESS_SEARCH_DEFAULTS.MAX_RESULTS,
  apiEndpoint = "/api/business-search",
  manualEntryCheckboxLabel = "Enter business manually",
  allowManualEntry = true,
  searchMode = "postal-code",
  searchPlaceholder,
  searchLabel = "Search Business",
  businessTypeLabel = "Business Type",
  businessTypePlaceholder = "Select business type",
  businessNameLabel = "Name of Business",
  businessNamePlaceholder,
  addressLine1Label = "Address Line 1",
  addressLine1Placeholder,
  addressLine2Label = "Address Line 2",
  addressLine2Placeholder,
  cityLabel = "City/Town",
  cityPlaceholder,
  stateLabel,
  statePlaceholder,
  postalCodeLabel = "Zip/Postal Code",
  postalCodePlaceholder,
  noResultsMessage = "No businesses found. Try entering your business manually using the checkbox below.",
  errorFallbackMessage = "Unable to search businesses. Please try again later or enter your business manually using the checkbox below.",
  errorMessageOverride,
  selectedBusinessHeading = "Selected Business:",
  showSelectedBusinessType = true,
  selectedBusinessDismissLabel = "Dismiss business",
  selectedBusinessNotCorrectLabel = "Not correct?",
  selectedBusinessTryAgainLabel = "Try again",
  inputParamName = "input",
  countryParamName = "country",
  businessTypeParamName = "businessType",
  businessTypeFilterOptions = "",
  srMessages,
  className,
  ...htmlProps
}: BusinessSearchFieldProps<TFieldValues, TName>) {
  // Use consolidated state management hook
  const { state, actions, refs } = useAddressSearchState();
  const {
    searchQuery,
    predictions: rawPredictions,
    showSuggestions,
    isLoading,
    hasSearched,
    hasError,
    errorMessage,
    selectedAddress: selectedBusiness,
    manualEntry,
    activeIndex,
    hasTyped,
  } = state;

  // Type cast predictions for business data
  const predictions = rawPredictions as unknown as BusinessPrediction[];

  // Destructure refs for easier access
  const debounceTimerRef = refs.debounceTimer;
  const abortControllerRef = refs.abortController;
  const queryCache = refs.queryCache;

  // Additional refs not in state hook
  const searchRef = React.useRef<HTMLDivElement>(null);

  const baseFieldName = name as string;
  const listboxId = React.useId();
  const activeDescendantId =
    activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;

  // Adaptive debounce: shorter delay after first keystroke for better UX
  const adaptiveDebounceMs = hasTyped
    ? ADDRESS_SEARCH_TIMING.ADAPTIVE_DEBOUNCE_MS
    : searchDebounceMs;

  // Computed mode value - derived from state instead of duplicate state
  const mode: "search" | "manual" | "selected" = selectedBusiness
    ? "selected"
    : manualEntry
      ? "manual"
      : "search";

  // Set default placeholders based on search mode and country
  const defaultSearchPlaceholder =
    searchMode === "postal-code"
      ? country === "US"
        ? "Enter ZIP code"
        : country === "CA"
          ? "Enter postal code"
          : country === "GB"
            ? "Enter postcode"
            : "Enter postal code"
      : "Start typing business name...";

  const finalSearchPlaceholder = searchPlaceholder ?? defaultSearchPlaceholder;
  const finalBusinessNamePlaceholder =
    businessNamePlaceholder ?? "Enter business name";
  const finalAddressLine1Placeholder =
    addressLine1Placeholder ?? "123 Main Street";
  const finalAddressLine2Placeholder = addressLine2Placeholder ?? "Suite 100";
  const finalCityPlaceholder = cityPlaceholder ?? "New York";
  const finalStatePlaceholder = statePlaceholder ?? "Select state";
  const finalPostalCodePlaceholder = postalCodePlaceholder ?? "12345";
  const finalBusinessTypePlaceholder = businessTypePlaceholder;

  const assistiveMessages = {
    searching: srMessages?.searching ?? ADDRESS_SEARCH_MESSAGES.SEARCHING,
    noResults: srMessages?.noResults ?? ADDRESS_SEARCH_MESSAGES.NO_RESULTS,
    error: srMessages?.error ?? ADDRESS_SEARCH_MESSAGES.ERROR_LOADING,
    resultsFound:
      srMessages?.resultsFound ?? ADDRESS_SEARCH_MESSAGES.RESULTS_FOUND,
  } satisfies Required<
    Pick<
      BusinessSearchAssistiveMessages,
      "searching" | "noResults" | "error" | "resultsFound"
    >
  >;

  // Use controller for validation field so we can update it properly
  const validationField = useController({
    control,
    name: `${baseFieldName}._validation` as FieldPath<TFieldValues>,
  });

  // Controllers for business fields to allow clearing on manual toggle
  const businessTypeController = useController({
    control,
    name: `${baseFieldName}.business.businessType` as FieldPath<TFieldValues>,
  });
  const nameController = useController({
    control,
    name: `${baseFieldName}.business.name` as FieldPath<TFieldValues>,
  });
  const line1Controller = useController({
    control,
    name: `${baseFieldName}.business.line1` as FieldPath<TFieldValues>,
  });
  const line2Controller = useController({
    control,
    name: `${baseFieldName}.business.line2` as FieldPath<TFieldValues>,
  });
  const cityController = useController({
    control,
    name: `${baseFieldName}.business.city` as FieldPath<TFieldValues>,
  });
  const stateController = useController({
    control,
    name: `${baseFieldName}.business.state` as FieldPath<TFieldValues>,
  });
  const postalCodeController = useController({
    control,
    name: `${baseFieldName}.business.postalCode` as FieldPath<TFieldValues>,
  });
  const crmIdController = useController({
    control,
    name: `${baseFieldName}.business.CRMId` as FieldPath<TFieldValues>,
  });
  const veevaNetworkIdController = useController({
    control,
    name: `${baseFieldName}.business.VeevaNetworkID` as FieldPath<TFieldValues>,
  });
  const accountClassificationController = useController({
    control,
    name: `${baseFieldName}.business.AccountClassification` as FieldPath<TFieldValues>,
  });

  // Controller for entry method to determine how data was entered
  const entryMethodController = useController({
    control,
    name: `${baseFieldName}.entryMethod` as FieldPath<TFieldValues>,
  });

  // Initialize internal state from form values on mount (for step navigation)
  React.useEffect(() => {
    const businessName = nameController.field.value;
    const businessType = businessTypeController.field.value;
    const line1 = line1Controller.field.value;
    const line2 = line2Controller.field.value;
    const city = cityController.field.value;
    const state = stateController.field.value;
    const postalCode = postalCodeController.field.value;
    const entryMethod = entryMethodController.field.value;

    // Check if we have business data from form (indicating previous selection)
    const hasBusinessData =
      businessName || line1 || city || state || postalCode;

    if (hasBusinessData) {
      // Reconstruct the selected business from form values
      const businessComponents = {
        businessType: businessType || "",
        name: businessName || "",
        line1: line1 || "",
        line2: line2 || "",
        city: city || "",
        state: state || "",
        postalCode: postalCode || "",
        CRMId: crmIdController.field.value || "",
      };

      // Set the selected address in internal state
      actions.setSelectedAddress(businessComponents);

      // Use entry method to determine if this was manual entry
      const isManualEntry = entryMethod === ENTRY_METHODS.MANUAL;
      actions.setManualEntry(isManualEntry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount to avoid conflicts with user interactions

  // Update validation field whenever selection state changes
  React.useEffect(() => {
    const newValue = selectedBusiness ? "valid" : manualEntry ? "manual" : "";
    validationField.field.onChange(newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness, manualEntry]);

  // Debounced search function with AbortController for race condition prevention
  const searchBusiness = React.useCallback(
    async (searchValue: string, signal: AbortSignal) => {
      // Validate and sanitize input
      const trimmed = searchValue.trim();

      // Validation: Check length constraints using constants
      if (
        !trimmed ||
        trimmed.length < ADDRESS_SEARCH_VALIDATION.MIN_QUERY_LENGTH ||
        trimmed.length > ADDRESS_SEARCH_VALIDATION.MAX_QUERY_LENGTH
      ) {
        actions.setPredictions([]);
        actions.setShowSuggestions(false);
        actions.setLoading(false);
        actions.setHasSearched(false);
        actions.setError(false, null);
        return;
      }

      // Basic sanitization: remove potentially dangerous characters
      const sanitized = trimmed.replace(/[<>]/g, "");

      // Create cache key from sanitized input, country, and business type filter
      const cacheKey = `${sanitized}:${country}:${businessTypeParamName}:${businessTypeFilterOptions}`;

      // Check cache first - deduplicate identical queries
      if (queryCache.current.has(cacheKey)) {
        const cachedResults = queryCache.current.get(cacheKey)!;
        actions.setPredictions(cachedResults);
        actions.setShowSuggestions(true);
        actions.setHasSearched(true);
        actions.setError(false, null);
        return;
      }

      actions.setLoading(true);
      actions.setError(false, null);

      try {
        // Create a timeout promise that will reject after configured timeout
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(
              new AddressSearchTimeoutError(
                ADDRESS_SEARCH_TIMING.REQUEST_TIMEOUT_MS,
              ),
            );
          }, ADDRESS_SEARCH_TIMING.REQUEST_TIMEOUT_MS);
        });

        // Race between fetch and timeout
        const response = await Promise.race([
          fetch(
            `${apiEndpoint}?${inputParamName}=${encodeURIComponent(sanitized)}&${countryParamName}=${country}${
              businessTypeFilterOptions
                ? `&${businessTypeParamName}=${encodeURIComponent(businessTypeFilterOptions)}`
                : ""
            }&limit=${maxResults}`,
            {
              signal, // Pass abort signal to fetch
            },
          ),
          timeoutPromise,
        ]);

        if (!response.ok) {
          throw new AddressSearchApiError(response.status, response.statusText);
        }

        const data = await response.json();

        // Check if request was aborted before updating state
        if (signal.aborted) return;

        if (data.data?.value && data.data.value.length > 0) {
          // Transform API response to BusinessPrediction format
          const transformedResults = data.data.value.slice(0, maxResults).map(
            (item: {
              id: string;
              Name: string;
              CRMId?: string;
              VeevaNetworkID?: string;
              AccountClassification?: string;
              location: {
                address: string;
                city: string;
                state: string;
                postalCode: string;
              };
            }) => ({
              business_id: item.id,
              CRMId: item.CRMId,
              VeevaNetworkID: item.VeevaNetworkID,
              name: item.Name,
              address: `${item.location.address}, ${item.location.city}, ${item.location.state} ${item.location.postalCode}`,
              AccountClassification: item.AccountClassification || "",
            }),
          );

          // Cache the results for future identical queries
          queryCache.current.set(cacheKey, transformedResults);

          // Use atomic action for success
          actions.searchSuccess(transformedResults);
        } else {
          // Use atomic action for no results
          actions.searchNoResults();
        }
      } catch (error) {
        // Ignore abort errors (they're expected when cancelling requests)
        if (isAbortError(error)) {
          return;
        }

        // Log the error with context
        logAddressSearchError(error, "searchBusiness", {
          searchValue: sanitized,
          country,
        });

        // Get user-friendly error message
        const friendlyMessage = getUserFriendlyErrorMessage(error);
        const finalErrorMessage = errorMessageOverride ?? friendlyMessage;

        // Don't update state if request was aborted
        if (!signal.aborted) {
          // Use atomic action for error
          actions.searchError(finalErrorMessage);
        }
      } finally {
        // Only update loading state if not aborted
        if (!signal.aborted) {
          actions.setLoading(false);
        }
      }
    },
    [
      apiEndpoint,
      country,
      maxResults,
      actions,
      queryCache,
      errorMessageOverride,
      inputParamName,
      countryParamName,
      businessTypeParamName,
      businessTypeFilterOptions,
    ],
  );

  // Debounced search on query change with request cancellation and adaptive timing
  React.useEffect(() => {
    // Mark that user has started typing (enables faster debounce)
    if (searchQuery && !hasTyped) {
      actions.setHasTyped(true);
    }

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear existing timeout
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Debounce the search with adaptive timing
    debounceTimerRef.current = setTimeout(() => {
      searchBusiness(searchQuery, abortController.signal);
    }, adaptiveDebounceMs);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      abortController.abort();
    };
    // debounceTimerRef and abortControllerRef are refs and stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchBusiness, adaptiveDebounceMs, hasTyped, actions]);

  // Close suggestions when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        actions.setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [actions]);

  // Handle business selection with useCallback to prevent re-renders
  const handleSelectBusiness = React.useCallback(
    async (businessId: string) => {
      try {
        const prediction = predictions.find(
          (p) => p.business_id === businessId,
        );

        if (!prediction) {
          throw new AddressSearchPredictionError(businessId);
        }

        // Parse the business data from the prediction
        // Business search API returns a flat structure, so we need to map it to our BusinessComponents structure
        const businessDetails: BusinessComponents = {
          businessType: prediction.AccountClassification || "",
          name: prediction.name,
          line1: prediction.address.split(", ")[0] || "",
          line2: "",
          city: prediction.address.split(", ")[1] || "",
          state: prediction.address.split(", ")[2]?.split(" ")[0] || "",
          postalCode:
            prediction.address.split(", ")[2]?.split(" ").slice(1).join(" ") ||
            "",
          CRMId: prediction.CRMId || "",
          AccountClassification: prediction.AccountClassification || "",
          VeevaNetworkID: prediction.VeevaNetworkID || "",
        };

        // BusinessComponents has compatible structure with AddressComponents
        // Both have line1, line2, city, state, postalCode properties
        actions.setSelectedAddress(
          businessDetails as unknown as typeof state.selectedAddress,
        );

        actions.setShowSuggestions(false);
        actions.setSearchQuery("");
        actions.setHasTyped(false);
      } catch (error) {
        // Log the error with context
        logAddressSearchError(error, "handleSelectBusiness", {
          businessId,
        });

        // Get user-friendly error message
        const friendlyMessage = getUserFriendlyErrorMessage(error);

        // Show error to user
        actions.setError(true, friendlyMessage);
        actions.setShowSuggestions(true);
      }
    },
    // state is stable from the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [predictions, actions],
  );

  // Handle removing selected business with useCallback
  const handleDismissBusiness = React.useCallback(() => {
    actions.dismissAddress();

    // Clear all business fields to prevent data inconsistency
    businessTypeController.field.onChange("");
    nameController.field.onChange("");
    line1Controller.field.onChange("");
    line2Controller.field.onChange("");
    cityController.field.onChange("");
    stateController.field.onChange("");
    postalCodeController.field.onChange("");
    crmIdController.field.onChange("");
    veevaNetworkIdController.field.onChange("");
    accountClassificationController.field.onChange("");

    // Clear entry method to reset to default state
    entryMethodController.field.onChange("");
  }, [
    actions,
    businessTypeController,
    nameController,
    line1Controller,
    line2Controller,
    cityController,
    stateController,
    postalCodeController,
    crmIdController,
    veevaNetworkIdController,
    accountClassificationController,
    entryMethodController,
  ]);

  // Handle manual entry toggle with useCallback
  const handleManualEntryToggle = React.useCallback(
    (checked: boolean) => {
      actions.setManualEntry(checked);
      if (checked) {
        actions.resetSearch();
        actions.setSelectedAddress(null);

        // Clear all business fields when switching to manual mode
        businessTypeController.field.onChange("");
        nameController.field.onChange("");
        line1Controller.field.onChange("");
        line2Controller.field.onChange("");
        cityController.field.onChange("");
        stateController.field.onChange("");
        postalCodeController.field.onChange("");
        crmIdController.field.onChange("");
        veevaNetworkIdController.field.onChange("");
        accountClassificationController.field.onChange("");
      }
    },
    [
      actions,
      businessTypeController,
      nameController,
      line1Controller,
      line2Controller,
      cityController,
      stateController,
      postalCodeController,
      crmIdController,
      veevaNetworkIdController,
      accountClassificationController,
    ],
  );

  return (
    <div className={cn("space-y-4", className)} {...htmlProps}>
      {/* Main Label and Description */}
      <div>
        <h3
          id={`${baseFieldName}-label`}
          className="text-sm leading-snug font-medium"
        >
          {label}
          {required && (
            <>
              <span className="ms-1 text-red-500" aria-hidden="true">
                *
              </span>
              <span className="sr-only">({requiredLabel})</span>
            </>
          )}
          {description && descriptionAsTooltip && (
            <FormTooltip description={description} />
          )}
        </h3>
        {description && !descriptionAsTooltip && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>

      {/* Hidden field to track entry method */}
      <HiddenField
        control={control}
        name={`${baseFieldName}.entryMethod` as FieldPath<TFieldValues>}
        value={manualEntry ? ENTRY_METHODS.MANUAL : ENTRY_METHODS.AUTOMATIC}
      />

      {/* Hidden validation field - ensures form validation works */}
      <FormItem className="hidden">
        <FormControl>
          <input type="hidden" {...validationField.field} />
        </FormControl>
      </FormItem>

      {/* Selected Business Display */}
      {mode === "selected" && selectedBusiness && (
        <div className="space-y-4">
          <div className="rounded-md border bg-muted/50 p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-1 items-start gap-3">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="font-medium text-foreground">
                    {selectedBusinessHeading}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-foreground">
                    {(selectedBusiness as BusinessComponents).name}
                  </p>
                  {showSelectedBusinessType &&
                    (selectedBusiness as BusinessComponents).businessType && (
                      <p className="text-sm text-muted-foreground">
                        {(selectedBusiness as BusinessComponents).businessType}
                      </p>
                    )}
                  <p className="mt-1 text-sm text-foreground">
                    {(selectedBusiness as BusinessComponents).line1}
                  </p>
                  {(selectedBusiness as BusinessComponents).line2 && (
                    <p className="text-sm text-foreground">
                      {(selectedBusiness as BusinessComponents).line2}
                    </p>
                  )}
                  <p className="text-sm text-foreground">
                    {(selectedBusiness as BusinessComponents).city},{" "}
                    {(selectedBusiness as BusinessComponents).state}{" "}
                    {(selectedBusiness as BusinessComponents).postalCode}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleDismissBusiness}
                className="shrink-0"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">{selectedBusinessDismissLabel}</span>
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {selectedBusinessNotCorrectLabel}{" "}
            <button
              type="button"
              onClick={handleDismissBusiness}
              className="font-medium text-primary hover:underline"
            >
              {selectedBusinessTryAgainLabel}
            </button>
          </p>
        </div>
      )}

      {/* Search Mode */}
      {mode === "search" && (
        <div className="space-y-4">
          <div ref={searchRef} className="relative">
            <Label htmlFor={`${baseFieldName}-search`} className="sr-only">
              {searchLabel}
            </Label>
            <div className="relative w-full">
              <Input
                id={`${baseFieldName}-search`}
                type="text"
                role="combobox"
                aria-expanded={showSuggestions && predictions.length > 0}
                aria-controls={listboxId}
                aria-activedescendant={activeDescendantId}
                aria-autocomplete="list"
                placeholder={finalSearchPlaceholder}
                value={searchQuery}
                onChange={(e) => {
                  actions.setSearchQuery(e.target.value);
                  actions.setActiveIndex(-1);
                }}
                onFocus={() => {
                  if (predictions.length > 0 || hasSearched || hasError) {
                    actions.setShowSuggestions(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (!showSuggestions || predictions.length === 0) return;

                  switch (e.key) {
                    case KEYBOARD_KEYS.ARROW_DOWN:
                      e.preventDefault();
                      actions.setActiveIndex(
                        activeIndex < predictions.length - 1
                          ? activeIndex + 1
                          : activeIndex,
                      );
                      break;
                    case KEYBOARD_KEYS.ARROW_UP:
                      e.preventDefault();
                      actions.setActiveIndex(
                        activeIndex > 0 ? activeIndex - 1 : -1,
                      );
                      break;
                    case KEYBOARD_KEYS.ENTER:
                      e.preventDefault();
                      if (activeIndex >= 0 && predictions[activeIndex]) {
                        handleSelectBusiness(
                          predictions[activeIndex].business_id,
                        );
                        actions.setActiveIndex(-1);
                      }
                      break;
                    case KEYBOARD_KEYS.ESCAPE:
                      e.preventDefault();
                      actions.setShowSuggestions(false);
                      actions.setActiveIndex(-1);
                      break;
                    case KEYBOARD_KEYS.HOME:
                      if (predictions.length > 0) {
                        e.preventDefault();
                        actions.setActiveIndex(0);
                      }
                      break;
                    case KEYBOARD_KEYS.END:
                      if (predictions.length > 0) {
                        e.preventDefault();
                        actions.setActiveIndex(predictions.length - 1);
                      }
                      break;
                  }
                }}
                className="pr-10"
              />
              {isLoading ? (
                <Loader2 className="pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 animate-spin text-muted-foreground" />
              ) : (
                <Search className="pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              )}
            </div>

            {showSuggestions && (
              <div
                id={listboxId}
                role="listbox"
                className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-md"
              >
                {predictions.length > 0 ? (
                  predictions.map((suggestion, index) => (
                    <div
                      key={suggestion.business_id}
                      id={`${listboxId}-option-${index}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      onClick={() => {
                        handleSelectBusiness(suggestion.business_id);
                        actions.setActiveIndex(-1);
                      }}
                      onMouseEnter={() => actions.setActiveIndex(index)}
                      className={`w-full cursor-pointer border-b px-4 py-3 text-left text-sm text-popover-foreground transition-colors last:border-b-0 ${
                        index === activeIndex ? "bg-accent" : "hover:bg-accent"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{suggestion.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {suggestion.address}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : hasError ? (
                  <div className="px-4 py-3 text-sm text-destructive">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{errorMessage || errorFallbackMessage}</span>
                    </div>
                  </div>
                ) : hasSearched ? (
                  <div className="px-4 py-3 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{noResultsMessage}</span>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Screen reader announcements */}
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            >
              {isLoading && assistiveMessages.searching}
              {!isLoading &&
                hasSearched &&
                predictions.length === 0 &&
                assistiveMessages.noResults}
              {!isLoading && hasError && assistiveMessages.error}
              {!isLoading &&
                predictions.length > 0 &&
                assistiveMessages.resultsFound(predictions.length)}
            </div>
          </div>
          <FormMessage>{validationField.fieldState.error?.message}</FormMessage>

          {allowManualEntry && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${baseFieldName}-manual-mode-search`}
                checked={manualEntry}
                onCheckedChange={handleManualEntryToggle}
              />
              <Label
                htmlFor={`${baseFieldName}-manual-mode-search`}
                className="cursor-pointer text-sm font-normal"
              >
                {manualEntryCheckboxLabel}
              </Label>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Mode */}
      {mode === "manual" && (
        <div className="space-y-4">
          {/* Business Type */}
          {businessTypeOptions.length > 0 && (
            <FormField
              control={control}
              name={
                `${baseFieldName}.business.businessType` as FieldPath<TFieldValues>
              }
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {businessTypeLabel}
                    {required && (
                      <>
                        <span className="ms-1 text-red-500" aria-hidden="true">
                          *
                        </span>
                        <span className="sr-only">({requiredLabel})</span>
                      </>
                    )}
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={finalBusinessTypePlaceholder}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {businessTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Business Name */}
          <FormField
            control={control}
            name={`${baseFieldName}.business.name` as FieldPath<TFieldValues>}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {businessNameLabel}
                  {required && (
                    <>
                      <span className="ms-1 text-red-500" aria-hidden="true">
                        *
                      </span>
                      <span className="sr-only">({requiredLabel})</span>
                    </>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={finalBusinessNamePlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Address Line 1 and 2 Row */}
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name={
                `${baseFieldName}.business.line1` as FieldPath<TFieldValues>
              }
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {addressLine1Label}
                    {required && (
                      <>
                        <span className="ms-1 text-red-500" aria-hidden="true">
                          *
                        </span>
                        <span className="sr-only">({requiredLabel})</span>
                      </>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={finalAddressLine1Placeholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address Line 2 */}
            {showAddressLine2 && (
              <FormField
                control={control}
                name={
                  `${baseFieldName}.business.line2` as FieldPath<TFieldValues>
                }
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {addressLine2Label}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={finalAddressLine2Placeholder}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* City and State Row */}
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name={`${baseFieldName}.business.city` as FieldPath<TFieldValues>}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {cityLabel}
                    {required && (
                      <>
                        <span className="ms-1 text-red-500" aria-hidden="true">
                          *
                        </span>
                        <span className="sr-only">({requiredLabel})</span>
                      </>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={finalCityPlaceholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name={
                `${baseFieldName}.business.state` as FieldPath<TFieldValues>
              }
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {stateLabel ||
                      (country === "CA"
                        ? "State/Province"
                        : country === "GB"
                          ? "County"
                          : "State/Province")}
                    {required && (
                      <>
                        <span className="ms-1 text-red-500" aria-hidden="true">
                          *
                        </span>
                        <span className="sr-only">({requiredLabel})</span>
                      </>
                    )}
                  </FormLabel>
                  {stateOptions && stateOptions.length > 0 ? (
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={finalStatePlaceholder} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {stateOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={finalStatePlaceholder}
                        {...field}
                      />
                    </FormControl>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Postal Code */}
          <FormField
            control={control}
            name={
              `${baseFieldName}.business.postalCode` as FieldPath<TFieldValues>
            }
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {postalCodeLabel}
                  {required && (
                    <>
                      <span className="ms-1 text-red-500" aria-hidden="true">
                        *
                      </span>
                      <span className="sr-only">({requiredLabel})</span>
                    </>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={finalPostalCodePlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Manual Entry Checkbox */}
          {allowManualEntry && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${baseFieldName}-manual-mode-manual`}
                checked={manualEntry}
                onCheckedChange={handleManualEntryToggle}
              />
              <Label
                htmlFor={`${baseFieldName}-manual-mode-manual`}
                className="cursor-pointer text-sm font-normal"
              >
                {manualEntryCheckboxLabel}
              </Label>
            </div>
          )}
        </div>
      )}

      {/* Hidden fields to store the selected business when using auto-lookup */}
      {selectedBusiness && mode === "selected" && (
        <>
          <HiddenField
            control={control}
            name={
              `${baseFieldName}.business.businessType` as FieldPath<TFieldValues>
            }
            value={(selectedBusiness as BusinessComponents).businessType}
          />
          <HiddenField
            control={control}
            name={`${baseFieldName}.business.name` as FieldPath<TFieldValues>}
            value={(selectedBusiness as BusinessComponents).name}
          />
          <HiddenField
            control={control}
            name={`${baseFieldName}.business.line1` as FieldPath<TFieldValues>}
            value={(selectedBusiness as BusinessComponents).line1}
          />
          <HiddenField
            control={control}
            name={`${baseFieldName}.business.line2` as FieldPath<TFieldValues>}
            value={(selectedBusiness as BusinessComponents).line2}
          />
          <HiddenField
            control={control}
            name={`${baseFieldName}.business.city` as FieldPath<TFieldValues>}
            value={(selectedBusiness as BusinessComponents).city}
          />
          <HiddenField
            control={control}
            name={`${baseFieldName}.business.state` as FieldPath<TFieldValues>}
            value={(selectedBusiness as BusinessComponents).state}
          />
          <HiddenField
            control={control}
            name={
              `${baseFieldName}.business.postalCode` as FieldPath<TFieldValues>
            }
            value={(selectedBusiness as BusinessComponents).postalCode}
          />
          <HiddenField
            control={control}
            name={`${baseFieldName}.business.CRMId` as FieldPath<TFieldValues>}
            value={(selectedBusiness as BusinessComponents).CRMId || ""}
          />
          <HiddenField
            control={control}
            name={
              `${baseFieldName}.business.AccountClassification` as FieldPath<TFieldValues>
            }
            value={
              (selectedBusiness as BusinessComponents).AccountClassification ||
              ""
            }
          />
          <HiddenField
            control={control}
            name={
              `${baseFieldName}.business.VeevaNetworkID` as FieldPath<TFieldValues>
            }
            value={
              (selectedBusiness as BusinessComponents).VeevaNetworkID || ""
            }
          />
        </>
      )}
    </div>
  );
}
